/**
 * Interior occlusion by explicit active-slot management (E3.3.S4, AD-158
 * constraint 6).
 *
 * Three.js has no automatic occlusion culling: while a dungeon interior is
 * entered, every exterior mesh in the same scene is still walked, frustum-tested
 * and — wherever the camera happens to face — submitted. The slot makes the
 * interior the ONLY graph the renderer draws.
 *
 * MECHANISM — camera layers, not `visible`. Every object in the slot's subtree
 * gains `ACTIVE_SLOT_LAYER` and the render camera is narrowed to that one layer.
 * Both renderers test `object.layers` against the camera's before projecting a
 * mesh (WebGLRenderer.projectObject, the common Renderer's _projectObject) and
 * the shadow pass tests the MAIN camera's layers too, so the exterior drops out
 * of the colour pass AND the shadow pass. Flipping `visible` instead was
 * rejected: game code toggles `visible` every frame (the move marker, telegraphs,
 * name tags), so a hide would be undone behind the slot's back, and an object
 * added mid-slot (a remote player arriving) would draw. Layers are not touched
 * anywhere else in the client, and a fresh object is born on layer 0 only — so
 * nothing outside the slot can leak in.
 *
 * LIGHTS ARE KEPT. Lights are layer-tested too; dropping the exterior's sun and
 * hemisphere would re-light the interior differently from how it was authored
 * and reviewed. Lights submit no draw calls of their own, and their shadow pass
 * only draws casters that pass the (narrowed) camera test, so keeping them costs
 * the exterior zero draw calls. Only top-level scene lights are kept — that is
 * where the scene builder places them.
 *
 * RELEASE restores exactly what entry changed: the camera's saved mask, and the
 * slot layer bit on only the objects entry granted it to. It is idempotent.
 *
 * Presentation-only and off the per-tick path. Import boundary (AD-122):
 * frontend-local.
 */

import * as THREE from 'three';

/** The layer the active slot renders on. Layer 0 is three's default for every object. */
export const ACTIVE_SLOT_LAYER = 1;

/** Grant the slot layer to every object in `root`'s subtree; returns those that lacked it. */
function grantLayer(root: THREE.Object3D, into: THREE.Object3D[]): void {
  root.traverse((object) => {
    if (!object.layers.isEnabled(ACTIVE_SLOT_LAYER)) {
      object.layers.enable(ACTIVE_SLOT_LAYER);
      into.push(object);
    }
  });
}

/**
 * Narrow `camera` to `slot`'s subtree (plus the scene's top-level lights).
 * `slot` must already be complete — an object added to it later is not drawn
 * until it is granted the layer. Returns the release; calling it twice is a no-op.
 */
export function enterActiveSlot(
  scene: THREE.Scene,
  camera: THREE.Camera,
  slot: THREE.Object3D,
): () => void {
  const savedMask = camera.layers.mask;
  const granted: THREE.Object3D[] = [];
  grantLayer(slot, granted);
  for (const child of scene.children) {
    if ((child as THREE.Light).isLight === true) grantLayer(child, granted);
  }
  camera.layers.set(ACTIVE_SLOT_LAYER);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    camera.layers.mask = savedMask;
    for (const object of granted) object.layers.disable(ACTIVE_SLOT_LAYER);
  };
}

export interface SubmittableCount {
  /** Meshes/lines/points outside `slot` the camera would submit (frustum ignored). */
  exterior: number;
  /** Meshes/lines/points inside `slot` the camera would submit. */
  slot: number;
}

/**
 * A CPU mirror of the renderer's projection gate — `visible` on the object and
 * every ancestor, and `layers.test(camera.layers)` on the object — counting the
 * drawables on each side of the slot. It deliberately ignores the frustum: the
 * claim is that the exterior CANNOT be submitted from any camera pose, not that
 * it happened to be behind the camera. An InstancedMesh counts once (one draw).
 * Published on the DEV scene dump next to the live `renderer.info` reading.
 */
export function countSubmittable(
  scene: THREE.Object3D,
  camera: THREE.Camera,
  slot: THREE.Object3D | null,
): SubmittableCount {
  const result: SubmittableCount = { exterior: 0, slot: 0 };
  const walk = (object: THREE.Object3D, inSlot: boolean): void => {
    if (!object.visible) return;
    const within = inSlot || object === slot;
    const drawable =
      (object as THREE.Mesh).isMesh === true ||
      (object as THREE.Line).isLine === true ||
      (object as THREE.Points).isPoints === true;
    if (drawable && object.layers.test(camera.layers)) {
      if (within) result.slot += 1;
      else result.exterior += 1;
    }
    for (const child of object.children) walk(child, within);
  };
  walk(scene, false);
  return result;
}
